import { createPortal } from 'react-dom';
import { commonStrings, messageStrings } from '../../../constants/strings';
import { Icon } from '../../Icon';
import { AiAnswerCopyButton } from '../AiAnswerCopyButton';

export type AiBatchResultItem = {
  timetoken: string;
  originalMessage: string;
  answer: string;
};

type Props = {
  results: AiBatchResultItem[];
  loading?: boolean;
  onClose: () => void;
  onRegenerate?: () => void;
};

export function AiSuggestionModal({
  results,
  loading = false,
  onClose,
  onRegenerate,
}: Props) {
  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex min-h-[100dvh] w-screen items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="mx-4 flex max-h-[80vh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <Icon
              icon="aiStar"
              width={20}
              height={20}
              className="text-blue-500"
            />
            <span className="text-base font-bold text-neutral-800">
              {messageStrings.aiSuggestTitle}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={commonStrings.close}
            className="rounded-lg p-1 text-neutral-400 transition-colors hover:bg-neutral-50 hover:text-neutral-600"
          >
            <Icon icon="close" width={20} height={20} />
          </button>
        </div>

        <div
          className="flex-1 overflow-y-auto px-6 py-4 space-y-4"
          style={{ scrollbarWidth: 'thin' }}
        >
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <div className="flex gap-1.5">
                <div className="w-2 h-2 rounded-full bg-blue-400 animate-bounce [animation-delay:-0.3s]" />
                <div className="w-2 h-2 rounded-full bg-blue-400 animate-bounce [animation-delay:-0.15s]" />
                <div className="w-2 h-2 rounded-full bg-blue-400 animate-bounce" />
              </div>
              <span className="text-sm text-blue-500 font-medium">
                {messageStrings.aiGenerating}
              </span>
            </div>
          ) : results.length === 0 ? (
            <div className="text-sm text-neutral-400 text-center py-8">
              {messageStrings.aiNoHistory}
            </div>
          ) : (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg px-4 py-4 border border-blue-100 space-y-4">
              {results.map((item, index) => (
                <div key={item.timetoken} className="space-y-1">
                  <div className="text-xs font-medium text-neutral-500">
                    Q{index + 1}. {item.originalMessage}
                  </div>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1 text-sm text-neutral-800 whitespace-pre-wrap leading-relaxed">
                      {item.answer}
                    </div>
                    <AiAnswerCopyButton text={item.answer} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {!loading && onRegenerate && results.length > 0 && (
          <div className="px-6 py-3 border-t border-neutral-100 flex justify-end shrink-0">
            <button
              type="button"
              onClick={onRegenerate}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50
               hover:bg-blue-100 rounded-lg transition-colors"
            >
              {messageStrings.aiRegenerate}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
